<template>
  <div class="users-container">
    <v-container fluid>
      <!-- Header -->
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="d-flex flex-column flex-md-row justify-space-between align-start align-md-center mb-4 ga-3">
            <h1 class="text-h4 font-weight-bold">Gestión de Usuarios</h1>
            <div class="d-flex ga-2">
              <v-btn 
                color="primary" 
                @click="showCreateUserDialog = true" 
                prepend-icon="mdi-account-plus"
                class="d-none d-sm-flex"
              >
                Nuevo Usuario
              </v-btn>
              <v-btn 
                color="primary" 
                @click="showCreateUserDialog = true" 
                icon="mdi-account-plus"
                class="d-sm-none"
                size="small"
              ></v-btn>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Estadísticas de usuarios -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="primary" size="40" class="mr-3">mdi-account-group</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ users.length }}</div>
                  <div class="text-subtitle-2 text-grey">Total Usuarios</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="success" size="40" class="mr-3">mdi-account-cog</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ adminCount }}</div>
                  <div class="text-subtitle-2 text-grey">Administradores</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="info" size="40" class="mr-3">mdi-account-hard-hat</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ operatorCount }}</div>
                  <div class="text-subtitle-2 text-grey">Operarios</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="warning" size="40" class="mr-3">mdi-account-eye</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ viewerCount }}</div>
                  <div class="text-subtitle-2 text-grey">Visualizadores</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tabla de usuarios -->
      <v-card>
        <v-card-title>
          <span class="text-h6">Usuarios del Sistema</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="loadUsers">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-card-title>
        
        <ResponsiveDataTable
          :headers="headers"
          :items="users"
          :loading="loading"
          class="users-table"
          item-key="id"
        >
          <template v-slot:item.role="{ item }">
            <v-chip :color="getRoleColor(item.role)" size="small">
              {{ getRoleText(item.role) }}
            </v-chip>
          </template>

          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn icon="mdi-dots-vertical" v-bind="props" size="small"></v-btn>
              </template>
              <v-list>
                <v-list-item @click="editUser(item)">
                  <v-list-item-title>Editar</v-list-item-title>
                </v-list-item>
                <v-list-item @click="deleteUser(item)" :disabled="item.role === 'admin' && adminCount <= 1">
                  <v-list-item-title>Eliminar</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </ResponsiveDataTable>
      </v-card>
    </v-container>

    <!-- Dialog para crear/editar usuario -->
    <v-dialog v-model="showCreateUserDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ editingUser ? 'Editar Usuario' : 'Nuevo Usuario' }}</span>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="userForm" v-model="userFormValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="userForm.username"
                  label="Nombre de Usuario *"
                  :rules="[v => !!v || 'Nombre de usuario es requerido']"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="userForm.email"
                  label="Email *"
                  type="email"
                  :rules="[
                    v => !!v || 'Email es requerido',
                    v => /.+@.+\..+/.test(v) || 'Email debe ser válido'
                  ]"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="userForm.password"
                  :label="editingUser ? 'Nueva Contraseña (opcional)' : 'Contraseña *'"
                  type="password"
                  :rules="editingUser ? [] : [v => !!v || 'Contraseña es requerida']"
                  :required="!editingUser"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="userForm.role"
                  :items="roleOptions"
                  label="Rol *"
                  :rules="[v => !!v || 'Rol es requerido']"
                  required
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="cancelUserDialog">Cancelar</v-btn>
          <v-btn color="primary" @click="saveUser" :disabled="!userFormValid">
            {{ editingUser ? 'Actualizar' : 'Crear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de confirmación para eliminar -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Confirmar Eliminación</v-card-title>
        <v-card-text>
          ¿Está seguro que desea eliminar al usuario <strong>{{ userToDelete?.username }}</strong>?
          Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showDeleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" @click="confirmDelete">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import api from '@/api'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'

export default {
  name: 'Users',
  components: {
    ResponsiveDataTable
  },
  data() {
    return {
      loading: false,
      users: [],
      
      // Dialogs
      showCreateUserDialog: false,
      showDeleteDialog: false,
      editingUser: null,
      userToDelete: null,
      
      // Form
      userFormValid: false,
      userForm: {
        username: '',
        email: '',
        password: '',
        role: 'operator'
      },
      
      // Opciones
      roleOptions: [
        { title: 'Administrador', value: 'admin' },
        { title: 'Operario', value: 'operator' },
        { title: 'Visualizador', value: 'viewer' },
        { title: 'Usuario', value: 'user' }
      ],
      
      // Headers de tabla
      headers: [
        { title: 'Usuario', key: 'username', sortable: true },
        { title: 'Email', key: 'email', sortable: true },
        { title: 'Rol', key: 'role', sortable: true },
        { title: 'Creado', key: 'created_at', sortable: true },
        { title: 'Acciones', key: 'actions', sortable: false }
      ],
      
      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },
  
  computed: {
    adminCount() {
      return this.users.filter(user => user.role === 'admin').length
    },
    operatorCount() {
      return this.users.filter(user => user.role === 'operator').length
    },
    viewerCount() {
      return this.users.filter(user => user.role === 'viewer').length
    }
  },
  
  async mounted() {
    await this.loadUsers()
  },
  
  methods: {
    async loadUsers() {
      this.loading = true
      try {
        const response = await api.getUsers()
        this.users = response.data
      } catch (error) {
        this.showSnackbar('Error al cargar usuarios', 'error')
        console.error('Error loading users:', error)
      } finally {
        this.loading = false
      }
    },
    
    editUser(user) {
      this.editingUser = user
      this.userForm = {
        username: user.username,
        email: user.email,
        password: '',
        role: user.role
      }
      this.showCreateUserDialog = true
    },
    
    async saveUser() {
      if (!this.userFormValid) return
      
      try {
        const userData = { ...this.userForm }
        
        // Si estamos editando y no hay contraseña, no la enviamos
        if (this.editingUser && !userData.password) {
          delete userData.password
        }
        
        if (this.editingUser) {
          await api.updateUser(this.editingUser.id, userData)
          this.showSnackbar('Usuario actualizado exitosamente')
        } else {
          await api.createUser(userData)
          this.showSnackbar('Usuario creado exitosamente')
        }
        
        this.cancelUserDialog()
        await this.loadUsers()
      } catch (error) {
        const message = error.response?.data?.error || 'Error al guardar usuario'
        this.showSnackbar(message, 'error')
        console.error('Error saving user:', error)
      }
    },
    
    cancelUserDialog() {
      this.showCreateUserDialog = false
      this.editingUser = null
      this.userForm = {
        username: '',
        email: '',
        password: '',
        role: 'operator'
      }
    },
    
    deleteUser(user) {
      this.userToDelete = user
      this.showDeleteDialog = true
    },
    
    async confirmDelete() {
      try {
        await api.deleteUser(this.userToDelete.id)
        this.showSnackbar('Usuario eliminado exitosamente')
        this.showDeleteDialog = false
        this.userToDelete = null
        await this.loadUsers()
      } catch (error) {
        const message = error.response?.data?.error || 'Error al eliminar usuario'
        this.showSnackbar(message, 'error')
        console.error('Error deleting user:', error)
      }
    },
    
    // Utilidades
    getRoleColor(role) {
      const colors = {
        admin: 'success',
        operator: 'primary',
        viewer: 'warning',
        user: 'info'
      }
      return colors[role] || 'grey'
    },
    
    getRoleText(role) {
      const texts = {
        admin: 'Administrador',
        operator: 'Operario',
        viewer: 'Visualizador',
        user: 'Usuario'
      }
      return texts[role] || role
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('es-CL')
    },
    
    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color
      }
    }
  }
}
</script>

<style scoped>
.users-container {
  padding: 20px;
}

.stats-card {
  height: 100%;
}

.users-table {
  background-color: white;
}

.users-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.users-table >>> th {
  background-color: #f8f9fa !important;
  font-weight: bold !important;
  border-bottom: 2px solid #dee2e6 !important;
}

.users-table >>> td {
  border-bottom: 1px solid #dee2e6 !important;
  padding: 12px 16px !important;
}

.users-table >>> tr:hover {
  background-color: #f8f9fa !important;
}

/* Estilos responsivos */
@media (max-width: 768px) {
  .users-container {
    padding: 10px;
  }
  
  .text-h4 {
    font-size: 1.5rem !important;
    margin-bottom: 12px;
  }
  
  /* Cards de estadísticas más compactas */
  .stats-card .v-card-text {
    padding: 12px;
  }
  
  .stats-card .text-h4 {
    font-size: 1.25rem !important;
  }
  
  .stats-card .text-subtitle-2 {
    font-size: 11px;
  }
  
  /* Tabla responsiva */
  .v-card-title {
    padding: 12px 16px;
    font-size: 16px;
  }
  
  .v-card-text {
    padding: 12px;
  }
  
  /* Chips más pequeños */
  .v-chip {
    font-size: 10px;
    height: 20px;
  }
  
  /* Botones más pequeños */
  .v-btn--size-small {
    min-width: 32px;
    height: 32px;
  }
  
  /* Modales responsivos */
  .v-dialog .v-card {
    margin: 8px;
  }
  
  .v-dialog .v-card-title {
    padding: 8px 12px;
    font-size: 14px;
  }
  
  .v-dialog .v-card-text {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .users-container {
    padding: 8px;
  }
  
  .text-h4 {
    font-size: 1.25rem !important;
    margin-bottom: 8px;
  }
  
  /* Cards de estadísticas para móviles pequeños */
  .stats-card .v-card-text {
    padding: 8px;
  }
  
  .stats-card .text-h4 {
    font-size: 1rem !important;
  }
  
  .stats-card .text-subtitle-2 {
    font-size: 10px;
  }
  
  .stats-card .v-icon {
    font-size: 24px !important;
  }
  
  /* Tabla para móviles pequeños */
  .v-card {
    margin-bottom: 12px;
  }
  
  .v-card-title {
    padding: 8px 12px;
    font-size: 14px;
  }
  
  .v-card-text {
    padding: 8px;
  }
  
  /* Chips más pequeños */
  .v-chip {
    font-size: 9px;
    height: 18px;
  }
  
  /* Botones más compactos */
  .v-btn {
    font-size: 11px;
    min-height: 28px;
  }
  
  /* Modales para móviles pequeños */
  .v-dialog .v-card {
    margin: 4px;
    border-radius: 8px;
  }
  
  .v-dialog .v-card-title {
    padding: 6px 8px;
    font-size: 13px;
  }
  
  .v-dialog .v-card-text {
    padding: 8px;
  }
  
  /* Formularios responsivos */
  .v-text-field, .v-select {
    font-size: 12px;
  }
  
  .v-field__input {
    font-size: 12px;
  }
}
</style> 